import { httpsCallable } from "firebase/functions";
import { isBooleanTrue, isObjectNotEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { functions } from ".";
import Activity from "../models/Activity";

const REFERRAL_INVITE_CTA_URL = 'https://app.readyrx.com/account/referrals';

const replaceReferralInviteTemplateFields = (content, fields = {}) => {
    return content.replace(
        /{{(\w*)}}/g,
        function (m, key) {
            return fields.hasOwnProperty(key) ? fields[key] : "";
        }
    );
};

const replaceReferralInviteCta = (content, referralLink) => {
    if (isStringNotEmpty(content) && isStringNotEmpty(referralLink)) {
        return content.split(REFERRAL_INVITE_CTA_URL).join(referralLink);
    }

    return content;
};

export const sendPasswordResetLink = async (toEmail) => {
    try {
        if (isStringNotEmpty(toEmail)) {
            const func = httpsCallable(functions, "sendpasswordresetlink", { timeout: 6000000 });
            const res = await func({ toEmail: toEmail });

            return isBooleanTrue(res?.data?.success);
        }
    } catch (error) {
        console.log(error);
    }

    return false;
};

export const sendEmailVerificationLink = async (toEmail, name) => {
    try {
        if (isStringNotEmpty(toEmail)) {
            const func = httpsCallable(functions, "sendemailverificationlink", { timeout: 6000000 });
            const res = await func({ toEmail: toEmail, name: name });

            return isBooleanTrue(res?.data?.success);
        }
    } catch (error) {
        console.log(error);
    }

    return false;
};

export const sendSignInWithEmailLink = async (toEmail, signInUrl) => {
    try {
        if (isStringNotEmpty(toEmail)) {
            const func = httpsCallable(functions, "sendsigninwithemaillink", { timeout: 6000000 });
            const res = await func({ toEmail: toEmail, signInUrl: signInUrl });

            return isBooleanTrue(res?.data?.success);
        }
    } catch (error) {
        console.log(error);
    }

    return false;
};

export const sendEmail = async (toEmail, name, subject, header, message, userId = null) => {
    try {
        if (isStringNotEmpty(toEmail) && isStringNotEmpty(subject) && isStringNotEmpty(header) && isStringNotEmpty(message)) {
            const func = httpsCallable(functions, "sendemail", { timeout: 6000000 });
            const res = await func({ toEmail: toEmail, name: name, subject: subject, header: header, message: message });

            const success = isBooleanTrue(res?.data?.success);
            const messageId = res?.data?.messageId;
            console.log('messageId', messageId);
            if (isBooleanTrue(success) && isStringNotEmpty(userId)) {
                await new Activity({
                    userId: userId,
                    type: 'EMAIL',
                    direction: 'OUTGOING',
                    outcome: 'SENT',
                    details: message,
                    emailId: messageId,
                    templateId: 'GENERIC'
                }).create();
            }

            return { success: success, messageId: messageId };
        }
    } catch (error) {
        console.log(error);
    }

    return { success: false, messageId: null };
};

export const sendEmailFromSupport = async (toEmail, name, subject, header, message, userId = null) => {
    try {
        if (isStringNotEmpty(toEmail) && isStringNotEmpty(subject) && isStringNotEmpty(header) && isStringNotEmpty(message)) {
            const func = httpsCallable(functions, "sendemailfromsupport", { timeout: 6000000 });
            const res = await func({ toEmail: toEmail, name: name, subject: subject, header: header, message: message });

            const success = isBooleanTrue(res?.data?.success);
            const messageId = res?.data?.messageId;
            console.log('messageId', messageId);
            if (isBooleanTrue(success) && isStringNotEmpty(userId)) {
                await new Activity({
                    userId: userId,
                    type: 'EMAIL',
                    direction: 'OUTGOING',
                    outcome: 'SENT',
                    details: message,
                    emailId: messageId,
                    templateId: 'd-5735a46320a54699b1fd7edd4d8eafc5'
                }).create();
            }

            return { success: success, messageId: messageId };
        }
    } catch (error) {
        console.log(error);
    }

    return { success: false, messageId: null };
};

export const replyFromSharedGmail = async ({
    emailId,
    toEmail,
    ccEmails = [],
    bccEmails = [],
    subject,
    messageHtml,
    messageText,
    senderAddress = null,
    createTicket = false,
    ticketCategory = null,
    ticketSubCategory = null,
    ticketInitialStatus = 'PENDING',
    attachments = []
}) => {
    try {
        if (isStringNotEmpty(subject) && (isStringNotEmpty(messageHtml) || isStringNotEmpty(messageText))) {
            const func = httpsCallable(functions, "replyfromsharedgmail", { timeout: 6000000 });
            const res = await func({
                emailId,
                toEmail,
                ccEmails,
                bccEmails,
                subject,
                messageHtml,
                messageText,
                senderAddress,
                createTicket,
                ticketCategory,
                ticketSubCategory,
                ticketInitialStatus,
                attachments
            });

            return res?.data ?? { success: false, messageId: null };
        }
    } catch (error) {
        console.log(error);
    }

    return { success: false, messageId: null };
};

export const sendFromSharedGmail = async ({
    toEmail,
    ccEmails = [],
    bccEmails = [],
    subject,
    messageHtml,
    messageText,
    senderAddress = null,
    attachments = []
}) => {
    try {
        if (isStringNotEmpty(toEmail) && isStringNotEmpty(subject) && (isStringNotEmpty(messageHtml) || isStringNotEmpty(messageText))) {
            const func = httpsCallable(functions, "sendfromsharedgmail", { timeout: 6000000 });
            const res = await func({
                toEmail,
                ccEmails,
                bccEmails,
                subject,
                messageHtml,
                messageText,
                senderAddress,
                attachments
            });

            return res?.data ?? { success: false, messageId: null };
        }
    } catch (error) {
        console.log(error);
    }

    return { success: false, messageId: null };
};

export const sendTemplateEmail = async (template, userId, toEmail, name, subject, header, message, fromEmail, fromName, replyToEmail, replyToName) => {
    try {
        if (isStringNotEmpty(toEmail) && isStringNotEmpty(subject) && isStringNotEmpty(header) && isStringNotEmpty(message)) {
            const func = httpsCallable(functions, "sendtemplateemail", { timeout: 6000000 });
            const res = await func({
                template: template,
                userId: userId,
                toEmail: toEmail,
                name: name,
                subject: subject,
                header: header,
                message: message,
                fromEmail: fromEmail,
                fromName: fromName,
                replyToEmail: replyToEmail,
                replyToName: replyToName
            });

            return res?.data ?? { success: false, messageId: null };
        }
    } catch (error) {
        console.log(error);
    }

    return { success: false, messageId: null };
};

/**
 * Sends a referral invite email using a template.
 *
 * @param {object} params - The parameters for sending the referral invite email.
 * @param {object} params.template - The email template object containing subject, header, message, and sender info.
 * @param {string} params.userId - The ID of the user sending the referral.
 * @param {string} params.toEmail - The recipient's email address.
 * @param {string} [params.name=''] - The recipient's name.
 * @param {string} params.referralFullName - The full name of the person sending the referral.
 * @param {string} params.referralLink - The referral link to include in the email.
 * @param {string} params.couponCode - The coupon code associated with the referral.
 * @returns {Promise<{success: boolean, messageId: string|null, errorCode?: string}>} The result of the email send operation.
 */
export const sendReferralInviteEmail = async ({
    template,
    userId,
    toEmail,
    name = '',
    referralFullName,
    referralLink,
    couponCode
}) => {
    try {
        if (isObjectNotEmpty(template) && isStringNotEmpty(toEmail) && isStringNotEmpty(referralFullName) && isStringNotEmpty(referralLink)) {
            const templateFields = {
                couponCode,
                email: toEmail,
                firstName: name,
                link: referralLink,
                name,
                referralFullName,
                referralInviteLink: referralLink
            };
            const subject = replaceReferralInviteTemplateFields(template.subject ?? '', templateFields) || `${referralFullName} has invited you to join ReadyRx!`;
            const header = replaceReferralInviteTemplateFields(template.header ?? '', templateFields) || 'You have been invited to join ReadyRx!';
            const message = replaceReferralInviteCta(
                replaceReferralInviteTemplateFields(template.message ?? '', templateFields),
                referralLink
            );

            if (!isStringNotEmpty(message)) {
                return { success: false, messageId: null, errorCode: 'MISSING_MESSAGE' };
            }

            return await sendTemplateEmail(
                template,
                userId,
                toEmail,
                name,
                subject,
                header,
                message,
                template.fromEmail,
                template.fromName,
                template.replyToEmail,
                template.replyToName
            );
        }
    } catch (error) {
        console.log(error);
    }

    return { success: false, messageId: null };
};

// WHEN A USER REGISTERS FOR AN ACCOUNT
export const sendWelcomeEmail = async (toEmail, name, userId) => {
    try {
        if (isStringNotEmpty(toEmail) && isStringNotEmpty(name)) {
            // console.log('started sending welcome email');
            // console.log(moment().toISOString());
            const func = httpsCallable(functions, "sendwelcomeemail", { timeout: 6000000 });
            let subject = `Welcome to ReadyRx`;
            let header = `Welcome ${name}!`;
            let message = `
                Hi ${name},
                <br><br>
                Welcome to ReadyRx! Your account on ReadyRx has now been setup.
                <br><br>
                To activate the account please click on the button below that says, “Verify Account and Set Password” and then follow the instructions on the screen.
                If you believe you have received this email in error, please let us know immediately.
                <br><br><br>
                Sincerely,
                <br><br>
                ReadyRx
            `;
            const res = await func({ toEmail: toEmail, name: name, subject: subject, message: message, header: header, userId: userId });

            const success = isBooleanTrue(res?.data?.success);
            const messageId = res?.data?.messageId;
            console.log('messageId', messageId);
            if (isBooleanTrue(success)) {
                // console.log('sent welcome email');
                // console.log(moment().toISOString());
                await new Activity({
                    userId: userId,
                    type: 'EMAIL',
                    direction: 'OUTGOING',
                    outcome: 'SENT',
                    details: message,
                    emailId: messageId,
                    templateId: 'WELCOME'
                }).create();
                // console.log('created activity welcome email');
                // console.log(moment().toISOString());

                return true;
            }
        }
    } catch (error) {
        console.log(error);
    }

    return false;
};

export const sendEmailToIT = async (subject, body) => {
    try {
        if (isStringNotEmpty(subject) && isStringNotEmpty(body)) {
            const func = httpsCallable(functions, "sendemailtoit", { timeout: 6000000 });
            const res = await func({
                subject: subject,
                body: body
            });

            return res?.data ?? false;
        }
    } catch (error) {
        console.log(error);
    }

    return false;
};
